import { useEffect, useRef, useState } from 'react'
import type { FieldValues, UseFormWatch } from 'react-hook-form'
import {
  loadFormDraft,
  removeFormDraft,
  saveFormDraft,
} from '../shared/utils/formDraft'

/**
 * Configuration options for useFormLocalStorage hook
 */
export interface UseFormLocalStorageOptions<TFormData extends FieldValues> {
  /**
   * Storage key for localStorage (should follow 'votebox_' prefix convention)
   */
  key: string

  /**
   * react-hook-form's watch function to subscribe to form changes
   */
  watch?: UseFormWatch<TFormData>

  /**
   * Debounce delay in milliseconds before saving to localStorage
   * @default 1000
   */
  debounceMs?: number

  /**
   * Draft expiration time in milliseconds
   * @default 604800000 (7 days)
   */
  ttlMs?: number

  /**
   * Enable/disable persistence
   * @default true
   */
  enabled?: boolean
}

/**
 * Return value from useFormLocalStorage hook
 */
export interface UseFormLocalStorageReturn<TFormData extends FieldValues> {
  /**
   * Initial values loaded from localStorage (for react-hook-form defaultValues)
   */
  initialValues: Partial<TFormData> | null

  /**
   * Manually clear draft from localStorage
   */
  clearDraft: () => void

  /**
   * True if a draft was loaded on mount
   */
  hasDraft: boolean
}

const DEFAULT_DEBOUNCE_MS = 1000
const DEFAULT_TTL_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

/**
 * Hook for persisting react-hook-form data to localStorage with auto-save and restore
 *
 * @example
 * ```tsx
 * // 1. Load initial values before useForm
 * const { initialValues, clearDraft } = useFormLocalStorage<FormData>({
 *   key: 'votebox_new_box_form',
 *   debounceMs: 1000,
 *   ttlMs: 7 * 24 * 60 * 60 * 1000,
 * })
 *
 * // 2. Initialize form with restored values
 * const { watch, reset } = useForm<FormData>({
 *   defaultValues: {
 *     ...staticDefaults,
 *     ...initialValues,
 *   },
 * })
 *
 * // 3. Start auto-save after form is initialized
 * useFormLocalStorage<FormData>({
 *   key: 'votebox_new_box_form',
 *   watch,
 * })
 *
 * // 4. Clear draft after successful submission
 * const onSubmit = async (data: FormData) => {
 *   await submitForm(data)
 *   clearDraft()
 *   reset()
 * }
 * ```
 */
export function useFormLocalStorage<TFormData extends FieldValues>({
  key,
  watch,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  ttlMs = DEFAULT_TTL_MS,
  enabled = true,
}: UseFormLocalStorageOptions<TFormData>): UseFormLocalStorageReturn<TFormData> {
  // Load initial values on mount (only once)
  const [initialState] = useState(() => {
    if (!enabled) {
      return { initialValues: null, hasDraft: false }
    }

    const draft = loadFormDraft<TFormData>(key, ttlMs)
    return {
      initialValues: draft,
      hasDraft: draft !== null,
    }
  })

  // Track if this is the first change to avoid saving defaults immediately
  const isFirstChange = useRef(true)
  const debounceTimer = useRef<NodeJS.Timeout>()

  // Clear draft function
  const clearDraft = () => {
    if (enabled) {
      removeFormDraft(key)
    }
  }

  // Auto-save when form data changes (debounced)
  useEffect(() => {
    // Skip if disabled or no watch function provided
    if (!enabled || !watch) {
      return
    }

    // Subscribe to form changes using watch callback
    const subscription = watch(formData => {
      // Skip the first change event (triggered on subscription setup)
      if (isFirstChange.current) {
        isFirstChange.current = false
        return
      }

      // Clear previous debounce timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }

      // Set new debounce timer
      debounceTimer.current = setTimeout(() => {
        saveFormDraft(key, formData)
      }, debounceMs)
    })

    // Cleanup subscription and timer on unmount
    return () => {
      subscription.unsubscribe()
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
      // Reset first change flag to prevent race condition on remount
      isFirstChange.current = true
    }
  }, [watch, enabled, key, debounceMs])

  return {
    initialValues: initialState.initialValues,
    clearDraft,
    hasDraft: initialState.hasDraft,
  }
}
